/* IMPORTS */
import { CropfieldLayer } from "./CropfieldLayer";
import cropr from "../../../shared/utils/cropr";
import { RawLayer } from "./RawLayer";
import React, { useMemo } from "react";
import {
  DatasetTypes,
  MeasurementTypes,
  RoiTypes,
  SelectedMeasurementsObjectTypes,
} from "./ObjectInterfaces";

/* Function ordering the measurement data by name in alphabetical order */
const orderDatasetMeasurements = (m1: MeasurementTypes, m2: MeasurementTypes) => {
  function getSortKey(m: MeasurementTypes) {
    let name: string = m.name;
    let prio: number = m.prio;
    // @ts-ignore   [Proper TS Solution Needed]
    let isobusType: any = cropr.isobus_measurement_types[name.toLowerCase().split("__")[0]];
    return (m.device || "Z") + prio + (isobusType ? isobusType.name : name);
  }
  const m1SortKey: string = getSortKey(m1);
  const m2SortKey: string = getSortKey(m2);
  if (m1SortKey < m2SortKey) {
    return -1;
  }
  if (m1SortKey > m2SortKey) {
    return 1;
  }
  return 0;
};

/* Assigning the prop types */
interface LayerListProps {
  dataset: DatasetTypes;
  getDisplayName: any;
  layers: RoiTypes[];
  selectedCropfields: string[];
  selectedMeasurements: SelectedMeasurementsObjectTypes;
  setNumOfMeasurements: any;
  setSelectedMeasurements: any;
}

/* LayerList Component */
export const LayerList = ({
  dataset,
  getDisplayName,
  layers,
  selectedCropfields,
  selectedMeasurements,
  setNumOfMeasurements,
  setSelectedMeasurements,
}: LayerListProps) => {
  /* Function collecting the relevant measurement data from the dataset */
  const measurements = useMemo(() => {
    const ds: DatasetTypes = dataset;
    const measurements: MeasurementTypes[] = [];

    let datasetMeasurements: MeasurementTypes[] = ds.dataset_measurements;
    if (selectedMeasurements.length) {
      datasetMeasurements = datasetMeasurements.filter(
        // @ts-ignore   [Proper TS Solution Needed]
        (m: MeasurementTypes) => selectedMeasurements.indexOf(m.name) > -1
      );
    }
    datasetMeasurements.forEach((m: MeasurementTypes) => {
      const normalizedName: string = m.name.toLowerCase().split("__")[0];
      // @ts-ignore   [Proper TS Solution Needed]
      const machine: any = cropr.getDevice(m.device);
      if (machine && machine.get("priority_measurements")) {
        m.prio = machine.get("priority_measurements").indexOf(normalizedName) >= 0 ? 1 : 2;
      }
    });
    datasetMeasurements
      .sort(orderDatasetMeasurements)
      .forEach(function (measurement: MeasurementTypes) {
        const name: string = measurement.name.split("__")[0];
        measurement.display_name = getDisplayName(measurement.code, name, measurement.unit);
        // @ts-ignore   [Proper TS Solution Needed]
        measurement.machine_name = cropr.getDeviceName(measurement.device);
        measurements.push(measurement);
      });
    return measurements;
  }, [dataset, getDisplayName, selectedMeasurements]);

  /* Filtering out bad layer */
  let layersArr: RoiTypes[] = layers;
  if (selectedCropfields.length > 0) {
    layersArr = layersArr.filter((l: RoiTypes) => selectedCropfields.indexOf(l.roi) > -1);
  }

  /* JSX */
  const showRaw: boolean =
    ["n", "e"].indexOf(dataset.data_format) < 0 && selectedCropfields.length === 0;
  const components = showRaw ? [RawLayer, CropfieldLayer] : [CropfieldLayer];
  return (
    <div>
      {components.map((Component) =>
        layersArr.map((layer: RoiTypes) => (
          <Component
            dataset={dataset}
            key="rawlayer"
            layer={layer}
            measurements={measurements}
            selectedMeasurements={selectedMeasurements}
            setNumOfMeasurements={setNumOfMeasurements}
            setSelectedMeasurements={setSelectedMeasurements}
          />
        ))
      )}
    </div>
  );
};
